import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const BACKUP_KEY = 'csid_backup_history';
const MAX_BACKUPS = 10;

function fmtSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function fmtAge(ts) {
  const mins = Math.round((Date.now() - ts) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return mins + 'm ago';
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return hrs + 'h ago';
  return Math.round(hrs / 24) + 'd ago';
}

function getBackupHistory() {
  try { return JSON.parse(localStorage.getItem(BACKUP_KEY) || '[]'); } catch { return []; }
}

function saveBackupHistory(history) {
  try { localStorage.setItem(BACKUP_KEY, JSON.stringify(history)); } catch {}
}

export function createBackup(items, vendors, projects, label = 'auto', assemblies = []) {
  try {
    const snapshot = {
      id: Date.now(),
      label,
      ts: Date.now(),
      itemCount: (items || []).length,
      vendorCount: (vendors || []).length,
      projectCount: (projects || []).length,
      assemblyCount: (assemblies || []).length,
      size: 0,
      data: { items, vendors, projects, assemblies },
    };
    const json = JSON.stringify(snapshot);
    snapshot.size = json.length;

    const history = getBackupHistory();
    history.unshift(snapshot);
    // Keep only MAX_BACKUPS
    const trimmed = history.slice(0, MAX_BACKUPS);
    saveBackupHistory(trimmed);

    // Also save as the latest backup for quick restore
    localStorage.setItem('csid_latest_backup', JSON.stringify({
      ts: snapshot.ts,
      itemCount: snapshot.itemCount,
      data: snapshot.data,
    }));

    console.log(`Backup created: ${label} - ${snapshot.itemCount} items, ${fmtSize(snapshot.size)}`);
    return snapshot;
  } catch (e) {
    console.error('Backup failed:', e);
    if (e && e.name === 'QuotaExceededError') {
      if (window._setSaveError) window._setSaveError('⚠ Browser storage full — local backups paused. Export to JSON to free space.');
    }
    return null;
  }
}

export function getLatestBackup() {
  try {
    const d = localStorage.getItem('csid_latest_backup');
    return d ? JSON.parse(d) : null;
  } catch { return null; }
}

export default function DataProtection({ items, vendors, projects, assemblies, onRestore }) {
  const [backups, setBackups] = useState(getBackupHistory);
  const [supabaseStatus, setSupabaseStatus] = useState('checking'); // checking, ok, error
  const [supabaseError, setSupabaseError] = useState('');
  const [lastBackupTs, setLastBackupTs] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Check Supabase health on mount
  useEffect(() => {
    supabase.from('items').select('id').limit(1)
      .then(r => {
        if (r.error) {
          setSupabaseStatus('error');
          setSupabaseError(r.error.message);
        } else {
          setSupabaseStatus('ok');
        }
      })
      .catch(e => {
        setSupabaseStatus('error');
        setSupabaseError(e.message);
      });
  }, []);

  // Auto-backup every 5 minutes when items change
  useEffect(() => {
    if (!items || items.length === 0) return;
    const snapshot = createBackup(items, vendors, projects, 'auto', assemblies);
    if (snapshot) {
      setLastBackupTs(snapshot.ts);
      setBackups(getBackupHistory());
    }
  }, [items]);

  // Periodic backup every 5 minutes regardless of changes
  useEffect(() => {
    const interval = setInterval(() => {
      if (items && items.length > 0) {
        const snapshot = createBackup(items, vendors, projects, 'periodic', assemblies);
        if (snapshot) {
          setLastBackupTs(snapshot.ts);
          setBackups(getBackupHistory());
        }
      }
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [items, vendors, projects]);

  function manualBackup() {
    const label = 'manual-' + new Date().toLocaleTimeString();
    const snapshot = createBackup(items, vendors, projects, label, assemblies);
    if (snapshot) {
      setLastBackupTs(snapshot.ts);
      setBackups(getBackupHistory());
      alert(`Backup saved: ${snapshot.itemCount} items, ${fmtSize(snapshot.size)}`);
    }
  }

  function exportJSON() {
    setExporting(true);
    try {
      const exportData = {
        exportedAt: new Date().toISOString(),
        version: '1.0',
        items: items || [],
        vendors: vendors || [],
        projects: projects || [],
        assemblies: assemblies || [],
      };
      const json = JSON.stringify(exportData, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cimon-starr-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export failed: ' + e.message);
    }
    setExporting(false);
  }

  function importJSON(file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const data = JSON.parse(e.target.result);
        if (!data.items) throw new Error('Invalid backup file — no items found');
        if (!window.confirm(`Restore ${data.items.length} items from ${data.exportedAt?.slice(0, 10) || 'backup'}? This will replace your current data.`)) return;
        setRestoring(true);
        onRestore(data);
        setRestoring(false);
        alert(`Restored ${data.items.length} items. Syncing to database — keep this tab open for a few seconds.`);
      } catch (err) {
        alert('Could not read backup file: ' + err.message);
      }
    };
    reader.readAsText(file);
  }

  function restoreFromHistory(backup) {
    if (!window.confirm(`Restore to backup from ${new Date(backup.ts).toLocaleString()}?\n${backup.itemCount} items · This will replace your current data.`)) return;
    setRestoring(true);
    onRestore(backup.data);
    setRestoring(false);
    alert(`Restored ${backup.itemCount} items. Syncing to database — keep this tab open for a few seconds.`);
  }

  function deleteBackup(id) {
    const updated = backups.filter(b => b.id !== id);
    saveBackupHistory(updated);
    setBackups(updated);
  }

  const statusColor = {
    checking: '#888',
    ok: '#2e7d32',
    error: '#c62828',
  }[supabaseStatus];

  const statusLabel = {
    checking: 'Checking...',
    ok: 'Connected',
    error: 'Connection error',
  }[supabaseStatus];

  const totalBackupSize = backups.reduce((s, b) => s + (b.size || 0), 0);

  return (
    <div style={{ maxWidth: 800 }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Data Protection</div>
        <div style={{ fontSize: 13, color: '#666' }}>
          Automatic backups, export, and restore tools to keep your data safe.
        </div>
      </div>

      {/* Status cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
        {/* Supabase status */}
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#aaa', letterSpacing: 0.5, marginBottom: 6 }}>DATABASE</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor, flexShrink: 0 }} />
            <div style={{ fontSize: 14, fontWeight: 700, color: statusColor }}>{statusLabel}</div>
          </div>
          {supabaseError && <div style={{ fontSize: 10, color: '#c62828', marginTop: 4 }}>{supabaseError.slice(0, 60)}</div>}
        </div>

        {/* Last backup */}
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#aaa', letterSpacing: 0.5, marginBottom: 6 }}>LAST BACKUP</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: lastBackupTs ? '#2e7d32' : '#888' }}>
            {lastBackupTs ? fmtAge(lastBackupTs) : 'None yet'}
          </div>
          <div style={{ fontSize: 10, color: '#aaa', marginTop: 2 }}>
            {backups.length} snapshots · {fmtSize(totalBackupSize)}
          </div>
        </div>

        {/* Items count */}
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#aaa', letterSpacing: 0.5, marginBottom: 6 }}>IN MEMORY</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a' }}>
            {(items || []).length} items
          </div>
          <div style={{ fontSize: 10, color: '#aaa', marginTop: 2 }}>
            {(vendors || []).length} vendors · {(projects || []).length} projects
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
        <button onClick={manualBackup} style={{
          padding: '9px 18px', background: '#1a1a1a', color: '#fff',
          border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>
          💾 Save Backup Now
        </button>
        <button onClick={exportJSON} disabled={exporting} style={{
          padding: '9px 18px', background: '#fff', color: '#1a1a1a',
          border: '1px solid #ddd', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>
          📥 Export to JSON File
        </button>
        <label style={{
          padding: '9px 18px', background: '#fff', color: '#1a1a1a',
          border: '1px solid #ddd', borderRadius: 6, fontSize: 13, fontWeight: 600,
          cursor: 'pointer', display: 'inline-block',
        }}>
          📤 Restore from JSON File
          <input type="file" accept=".json" onChange={e => importJSON(e.target.files[0])}
            style={{ display: 'none' }} />
        </label>
      </div>

      {/* Supabase error guidance */}
      {supabaseStatus === 'error' && (
        <div style={{ background: '#ffebee', border: '1px solid #ef9a9a', borderRadius: 8, padding: '14px 16px', marginBottom: 20 }}>
          <div style={{ fontWeight: 700, color: '#c62828', marginBottom: 6 }}>Database connection issue</div>
          <div style={{ fontSize: 13, color: '#333', lineHeight: 1.6 }}>
            Your data is backed up locally and won't be lost. To fix:
            <br />1. Check your internet connection
            <br />2. Go to Supabase and run <code>supabase/migrate_items.sql</code>
            <br />3. Reload the app
          </div>
        </div>
      )}

      {/* Backup history */}
      <div>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
          <span>Backup history ({backups.length}/{MAX_BACKUPS})</span>
          {backups.length > 0 && (
            <span style={{ fontSize: 11, color: '#aaa', fontWeight: 400 }}>
              Stored in your browser · automatic every 5 min
            </span>
          )}
        </div>

        {backups.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px', border: '1px dashed #ddd', borderRadius: 8, color: '#aaa', fontSize: 13 }}>
            No backups yet — they'll appear here automatically as you work
          </div>
        ) : (
          <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  {['Time', 'Label', 'Items', 'Size', ''].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '1px solid #e0e0e0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {backups.map((b, i) => (
                  <tr key={b.id} style={{ borderBottom: '1px solid #f5f5f5', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '8px 12px', color: '#333', fontWeight: 500 }}>
                      {new Date(b.ts).toLocaleString()}
                      <div style={{ fontSize: 10, color: '#aaa' }}>{fmtAge(b.ts)}</div>
                    </td>
                    <td style={{ padding: '8px 12px', color: '#666' }}>
                      <span style={{
                        background: b.label === 'manual' || b.label.startsWith('manual') ? '#e8f5e9' : '#f5f5f5',
                        color: b.label.startsWith('manual') ? '#2e7d32' : '#888',
                        padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                      }}>
                        {b.label.startsWith('manual') ? 'Manual' : b.label.startsWith('periodic') ? 'Periodic' : 'Auto'}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', color: '#333' }}>{b.itemCount}</td>
                    <td style={{ padding: '8px 12px', color: '#666' }}>{fmtSize(b.size || 0)}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={() => restoreFromHistory(b)} style={{
                          padding: '3px 10px', background: '#e8f0fe', border: 'none',
                          borderRadius: 4, fontSize: 10, fontWeight: 600, cursor: 'pointer', color: '#1a56db',
                        }}>Restore</button>
                        <button onClick={() => {
                          const json = JSON.stringify({ ...b.data, exportedAt: new Date(b.ts).toISOString(), version: '1.0' }, null, 2);
                          const blob = new Blob([json], { type: 'application/json' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `cimon-starr-${b.label}-${new Date(b.ts).toISOString().slice(0, 10)}.json`;
                          document.body.appendChild(a); a.click(); document.body.removeChild(a);
                          URL.revokeObjectURL(url);
                        }} style={{
                          padding: '3px 10px', background: '#f5f5f5', border: '1px solid #ddd',
                          borderRadius: 4, fontSize: 10, cursor: 'pointer', color: '#555',
                        }}>Export</button>
                        <button onClick={() => deleteBackup(b.id)} style={{
                          padding: '3px 8px', background: '#ffebee', border: 'none',
                          borderRadius: 4, fontSize: 10, cursor: 'pointer', color: '#c62828',
                        }}>✕</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info box */}
      <div style={{ marginTop: 20, background: '#f5f5f3', borderRadius: 8, padding: '12px 16px', fontSize: 12, color: '#666', lineHeight: 1.7 }}>
        <strong>How backups work:</strong> Every change automatically saves a snapshot to your browser's local storage.
        Backups are also saved every 5 minutes. Use "Export to JSON" to download a file you can store anywhere (Google Drive, Dropbox etc).
        If your database is ever empty on reload, the app automatically restores from the most recent local backup.
        <br /><strong>Note:</strong> Browser backups are per-device. For team-wide safety, use "Export to JSON" regularly and save to a shared drive.
      </div>
    </div>
  );
}
